import React from 'react';
import styled from 'styled-components';
import Modal from '../../Modal/Modal';
import { H2, P } from '../../elements/Text';
import { CloseIcon } from '../../Icons';
import { useGetCompany } from '../../../hooks/use-hydra/admin';
import Button from '../../elements/Button';
import { toast } from 'react-toastify';
import { addLearners } from '../../../api/admin';
import { useMutation } from 'react-query';
import useAuth from '../../../hooks/use-auth';
import { AxiosError } from 'axios';
import Papa from 'papaparse';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { TableBody, TableCell } from '../../Table';
import { TableHead } from '../../Table';
import { TableRow } from '../../Table';
import { TableHeader } from '../../Table';
import { Table } from '../../Table';
import { TableWrapper } from '../../Table';
import { useParams } from 'react-router-dom';
const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

type NewLearner = {
  name: string;
  email: string;
};

const columnHelper = createColumnHelper<NewLearner>();

const EditableDiv = ({
  value,
  onBlur,
}: {
  value: string;
  onBlur: (e: React.FocusEvent<HTMLDivElement>) => void;
}) => (
  <EditableDivWrapper
    contentEditable
    onBlur={onBlur}
    suppressContentEditableWarning={true}
  >
    {value}
  </EditableDivWrapper>
);

const EditableDivWrapper = styled.div`
  width: 100%;
  overflow: hidden;

  &[contenteditable]:focus {
    outline: 0px solid transparent;
  }
`;

const StyledTableCell = styled(TableCell)`
  max-width: 50%;
  width: 50%;
  overflow: hidden;
  transition: background-color 0.3s ease-in-out;

  &:hover {
    background-color: ${({ theme }) => theme.colors.bgPage};
  }
`;

const DeleteButton = styled.div`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textDanger};
  cursor: pointer;
  font-size: 16px;
  padding: 4px 8px;

  &:hover {
    opacity: 0.8;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const EditableCell = ({
  getValue,
  row: { index },
  column: { id },
  table,
}: {
  getValue: () => string;
  row: { index: number };
  column: { id: string };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  table: any;
}) => {
  const initialValue = getValue();
  const [value, setValue] = React.useState(initialValue);

  const onBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const newValue = e.target.innerText;
    table.options.meta?.updateData?.(index, id, newValue);
  };

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return <EditableDiv value={value} onBlur={onBlur} />;
};

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: EditableCell,
  }),
  columnHelper.accessor('email', {
    header: 'Email',
    cell: EditableCell,
  }),
  columnHelper.display({
    id: 'actions',
    header: '',
    cell: ({ row, table }) => {
      return (
        <DeleteButton
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onClick={() => (table.options.meta as any)?.deleteRow?.(row.index)}
          title="Delete row"
        >
          <i className="fa-solid fa-trash"></i>
        </DeleteButton>
      );
    },
  }),
];

const AddLearnerRowButton = ({ onClick }: { onClick: () => void }) => (
  <Button
    type="secondary"
    label="Add Row"
    onClick={onClick}
    showIconBeforeLabel
    faIcon="fa-solid fa-plus"
    tooltip="Add one learner manually by entering their name and email"
  />
);

const ImportCSVButton = ({
  onFileUpload,
}: {
  onFileUpload: (text: string) => void;
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const onClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      onFileUpload(text);
    };
    reader.readAsText(file);
  };

  return (
    <React.Fragment>
      <Button
        type="secondary"
        label="Import CSV"
        onClick={onClick}
        faIcon="fa-solid fa-file-import"
        showIconBeforeLabel
        tooltip="The CSV must include two columns: name and email"
      />
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".csv"
        onChange={handleFileChange}
      />
    </React.Fragment>
  );
};

function parseLearners({
  existingLearners,
  newLearners,
}: {
  existingLearners: NewLearner[];
  newLearners: NewLearner[];
}): {
  validLearners: NewLearner[];
  invalidLearners: NewLearner[];
  existingLearners: NewLearner[];
} {
  return newLearners.reduce<{
    validLearners: NewLearner[];
    invalidLearners: NewLearner[];
    existingLearners: NewLearner[];
  }>(
    (acc, learner) => {
      if (!learner.name || !learner.email) {
        acc.invalidLearners.push(learner);
      } else if (existingLearners.find((l) => l.email === learner.email)) {
        acc.existingLearners.push(learner);
      } else {
        acc.validLearners.push(learner);
      }
      return acc;
    },
    { validLearners: [], invalidLearners: [], existingLearners: [] }
  );
}

const Wrapper = styled(Modal)`
  padding-bottom: 72px;
  padding-top: 64px;
  width: 80vw;
  min-width: 400px;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.bgPage2};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const CloseButton = styled(CloseIcon)`
  padding: 0;
`;

const Header = styled(H2)`
  margin: 0;
  padding: 0;
`;

export default function AddLearnersToCompanyModal({
  isOpen,
  onClose,
  companyId,
}: {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
}) {
  const {
    data,
    refetch: refetchCompany,
    isLoading: isLoadingCompany,
  } = useGetCompany(companyId);

  const { getTokenSilently } = useAuth();
  const { id } = useParams<{ id: string }>();

  const [newLearners, setNewLearners] = React.useState<NewLearner[]>([]);
  const { existingLearners, invalidLearners, validLearners } = parseLearners({
    existingLearners: data?.learners ?? [],
    newLearners,
  });

  const onAddRow = () => {
    setNewLearners([...newLearners, { name: '', email: '' }]);
  };

  const onDeleteRow = (rowIndex: number) => {
    setNewLearners(newLearners.filter((_, index) => index !== rowIndex));
  };

  const onUpdateData = (rowIndex: number, columnId: string, value: string) => {
    setNewLearners(
      newLearners.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...row,
            [columnId]: value,
          };
        }
        return row;
      })
    );
  };

  const onFileUpload = (text: string) => {
    const results = Papa.parse<NewLearner>(text, {
      header: true,
      skipEmptyLines: true,
    });

    const rows: NewLearner[] = results.data.map((row) => ({
      name: row.name || '',
      email: row.email || '',
    }));

    const isLearnerValid = (learner: NewLearner) => {
      return learner.name && learner.email;
    };

    const validLearners = rows.filter(isLearnerValid);
    const invalidLearners = rows.filter((learner) => !isLearnerValid(learner));

    if (validLearners.length === 0) {
      toast.error('Could not read any learners from the CSV');
      return;
    }

    toast.success(
      `Read ${validLearners.length} learners from the CSV.${
        invalidLearners.length > 0
          ? ` ${invalidLearners.length} invalid rows`
          : ''
      }`
    );

    setNewLearners([...newLearners, ...validLearners]);
  };

  const addLearnersMutation = useMutation({
    mutationFn: async () => {
      const token = await getTokenSilently();
      const response = await addLearners(token, {
        learners: validLearners,
        id,
      });
      return response;
    },
    onSuccess: async () => {
      await refetchCompany();
      toast.success('Learners added successfully');
      setNewLearners([]);
      onClose();
    },
    onError: (error: Error | AxiosError) => {
      toast.error(`Failed to add learners - ${error.message}`);
    },
  });

  const onSaveLearners = () => {
    addLearnersMutation.mutate();
  };
  if (!isOpen) {
    return null;
  }

  return (
    <Wrapper>
      <HeaderRow>
        <Header>Add Learners to {data?.name}</Header>
        <CloseButton onClick={onClose} />
      </HeaderRow>
      <Content>
        {newLearners.length === 0 ? (
          <P>
            No learners added yet. Import a CSV to add learners in bulk, or add
            learners one by one.
          </P>
        ) : (
          <P>
            {validLearners.length > 0 &&
              `Will add ${validLearners.length} learner(s). `}
            {invalidLearners.length > 0 &&
              ` ${invalidLearners.length} invalid row(s).`}
            {existingLearners.length > 0 &&
              ` ${existingLearners.length} learner(s) already exist in the company.`}
          </P>
        )}
        <Row>
          <AddLearnerRowButton onClick={onAddRow} />
          <ImportCSVButton onFileUpload={onFileUpload} />
        </Row>
        {newLearners.length > 0 && (
          <NewLearnersTable
            data={newLearners}
            onDeleteRow={onDeleteRow}
            onUpdateData={onUpdateData}
          />
        )}
      </Content>

      <Row>
        <Button
          type="primary"
          label="Add Learners"
          onClick={onSaveLearners}
          showIconBeforeLabel
          faIcon="fa-solid fa-save"
          isDisabled={validLearners.length < 1}
          tooltip="Add all valid learners to the company. No email invites are sent."
          isLoading={addLearnersMutation.isLoading || isLoadingCompany}
        />
      </Row>
    </Wrapper>
  );
}

const NewLearnersTable = ({
  data,
  onDeleteRow,
  onUpdateData,
}: {
  data: NewLearner[];
  onDeleteRow: (index: number) => void;
  onUpdateData: (index: number, columnId: string, value: string) => void;
}) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      deleteRow: onDeleteRow,
      updateData: onUpdateData,
    },
  });

  return (
    <TableWrapper>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <StyledTableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </StyledTableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableWrapper>
  );
};
